import * as Sentry from "@sentry/react";

import {
  BoatType,
  typeWithMotorOrSail,
} from "Store/interfaces/products/checkout/riskProducts/BoatProduct";
import {
  resetPriceCalculationFormState,
  updatePriceCalculationFormState,
} from "Store/priceCalculation/priceCalculationSlice";
import { Form, FormikProps } from "formik";
import { BOAT_ABOUT_STEP, BOAT_FIND_STEP, BOAT_PRICE_STEP } from "../boatSteps";

import { PaddedSurface } from "App/global-style";
import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { ContactUsAlert } from "Components/Form/Alert";
import { RadioButton, RadioButtonTrueFalse } from "Components/Form/RadioButton";
import {
  getOptions,
  getYesOrNoOptions,
} from "Components/Form/RadioButton/radioButtonUtils";
import { RecentDamages } from "Components/Form/RecentDamages";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { TextManager } from "Components/TextContext";
import {
  AlertDescription,
  AlertTitle,
} from "Components/TextContext/commonComponentEnums";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { nextStep } from "Store/steps/stepsSlice";
import { useProductTexts } from "Store/texts/hooks";
import { isDefined } from "Utils/functions";
import { getError } from "Utils/productUtils";
import React from "react";
import { useDispatch } from "react-redux";
import { CommonFormElements } from "./CommonFormElements";
import { CommonFormElementsMotorOrSail } from "./CommonFormElementsMotorOrSail";
import { MotorBoatInboardsFormElements } from "./MotorBoatInboardsFormElements";
import { MotorBoatOutboardsFormElements } from "./MotorBoatOutboardsFormElements";
import { Smaabaatregisteret } from "./Smaabaatregisteret";
import { BoatProductIntroFormValues } from "./boatProductIntroInterfaces";
import { validationSchema } from "./boatProductIntroValidation";

const productName = INSURANCE_NAME_VALUE.BOAT;

export const BoatProductIntro = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { getText } = TextManager;
  const [texts, lang] = useProductTexts(productName.toLowerCase());
  const checkPriceTexts = texts.formText.checkPrice;
  const checkOutTexts = texts.formText.checkout;
  const initialFormState = useFormState<BoatProductIntroFormValues>(
    BOAT_FIND_STEP.id,
    initialFormValues,
  );

  const handleSubmit = (form: BoatProductIntroFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        productName: productName,
        stepId: BOAT_FIND_STEP.id,
        formState: form,
      }),
    );

    dispatch(nextStep());
  };

  const resetFormsWhenBoatTypeChanges = (
    formProps: FormikProps<Partial<BoatProductIntroFormValues>>,
  ): void => {
    formProps.setFieldValue("brandAndModel", undefined);
    formProps.setFieldValue("modelYear", undefined);
    formProps.setFieldValue("lengthInFeet", undefined);
    formProps.setFieldValue("engineBrand", undefined);
    formProps.setFieldValue("engineHorsePower", undefined);
    formProps.setFieldValue("engineTopSpeed", undefined);
    formProps.setFieldValue("boatNotPristine", undefined);
    formProps.setFieldValue("registeredInSmaabaatregisteret", undefined);
    formProps.setFieldValue("motorNumber", undefined);
    formProps.setFieldValue("registrationNumber", undefined);
    formProps.setFieldValue("injuries", undefined);
    formProps.setFieldValue("injuryYear", undefined);

    formProps.setErrors({});
    formProps.setTouched({}, false);
    dispatch(resetPriceCalculationFormState(BOAT_ABOUT_STEP.id));
    dispatch(resetPriceCalculationFormState(BOAT_PRICE_STEP.id));
  };

  return (
    <Sentry.ErrorBoundary>
      <ProductIntroTile
        initialValues={initialFormState}
        onSubmit={handleSubmit}
        productBackendName={ProductBackendName.BOAT}
        validationSchema={validationSchema(checkOutTexts, lang)}
        header={getText("boatform.header.productintro")}
      >
        {(formProps: FormikProps<Partial<BoatProductIntroFormValues>>) => (
          <Form>
            <PaddedSurface>
              <RadioButton
                name="boatType"
                id="boatType"
                label={getText("boatform.label.boattype")}
                options={getOptions(checkPriceTexts.boatType, lang)}
                direction="vertical"
                error={getError("boatType", formProps)}
                onChange={() => resetFormsWhenBoatTypeChanges(formProps)}
              />
              {typeWithMotorOrSail(formProps.values.boatType as BoatType) && (
                <>
                  <RadioButtonTrueFalse
                    name="boatNotPristine"
                    id="boatNotPristine"
                    label={getText("boatform.label.boatnotpristine")}
                    options={getYesOrNoOptions(
                      "boatNotPristine-yes",
                      "boatNotPristine-no",
                    )}
                    direction="vertical"
                    error={getError("boatNotPristine", formProps)}
                  />
                  {formProps.values.boatNotPristine ===
                    BooleanFormType.TRUE && (
                    <ContactUsAlert
                      title={getText(AlertTitle.CALL_US)}
                      description={getText(AlertDescription.CANT_BUY_ONLINE)}
                      alertId="boat-can-not-be-bought-online"
                      variant="border"
                      marginBottom="32px"
                    />
                  )}
                </>
              )}
              {formProps.values.boatType !== BoatType.SMALL_BOAT &&
                formProps.values.boatNotPristine === BooleanFormType.FALSE && (
                  <RecentDamages
                    formProps={formProps}
                    productName={ProductBackendName.BOAT}
                  />
                )}

              <Smaabaatregisteret formProps={formProps} />

              {isDefined(formProps.values.boatType) &&
                formProps.values.registeredInSmaabaatregisteret !==
                  undefined && (
                  <>
                    {typeWithMotorOrSail(formProps.values.boatType) && (
                      <CommonFormElementsMotorOrSail
                        formProps={formProps}
                        formText={texts.formText.checkout}
                        lang={lang}
                      />
                    )}

                    {formProps.values.boatType ===
                      BoatType.MOTOR_BOAT_INBOARDS && (
                      <MotorBoatInboardsFormElements formProps={formProps} />
                    )}

                    {formProps.values.boatType ===
                      BoatType.MOTOR_BOAT_OUTBOARDS && (
                      <MotorBoatOutboardsFormElements
                        formProps={formProps}
                        formText={texts.formText.checkout}
                        lang={lang}
                      />
                    )}
                  </>
                )}
            </PaddedSurface>

            {isDefined(formProps.values.boatType) && (
              <CommonFormElements
                formProps={formProps}
                texts={checkPriceTexts}
                lang={lang}
              />
            )}

            <PartnerProductIntroForms isWithSurface />

            <ScrollToFirstError />

            <MarketingConsent />

            <NavigationButtons hidePreviousButton />
          </Form>
        )}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};

const initialFormValues: Partial<BoatProductIntroFormValues> = {
  boatNotPristine: undefined,
  boatType: undefined,
  brandAndModel: undefined,
  coverageAmount: undefined,
  engineBrand: undefined,
  engineHorsePower: undefined,
  engineTopSpeed: undefined,
  injuries: undefined,
  injuryYear: undefined,
  lengthInFeet: undefined,
  registeredInSmaabaatregisteret: undefined,
  modelYear: undefined,
  motorNumber: undefined,
  numberOfYearsAsOwner: undefined,
};
